import React from "react";
import { motion } from "framer-motion";

// Golf/Gym page language, in Soulumo's night-blue and amber.
export const S = {
  night: "#070D1D", deep: "#0D1830", navy: "#13233F", ivory: "#F5EEDF", sand: "#EAE0CB",
  ink: "#121C33", gold: "#E0A97A", goldSoft: "rgba(224,169,122,0.3)", cream: "#F5ECD9",
  dim: "rgba(245,236,217,0.68)", inkDim: "rgba(18,28,51,0.66)", line: "rgba(18,28,51,0.12)", bronze: "#9A6B45",
};
export const SERIF = '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif';
export const SANS = '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif';
export const IMG = {
  hero: "https://images.unsplash.com/photo-1634605615726-82f5c5d84a8b?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  create: "https://images.unsplash.com/photo-1614287151203-842d5b808bc5?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  listen: "https://images.unsplash.com/photo-1769467701165-a83217c13446?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  voice: "https://images.unsplash.com/photo-1665396695711-a9627aaf894e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  music: "https://images.unsplash.com/flagged/photo-1575388105878-0ff8d276d368?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  affirm: "https://images.unsplash.com/photo-1567516364473-233c4b6fcfbe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  night: "https://images.unsplash.com/photo-1658928784381-6f68bbcf92f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  day: "https://images.unsplash.com/photo-1758522489023-d95ecf789fe0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  together: "https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  togetherCard: "https://images.unsplash.com/photo-1530047139082-5435ca3c4614?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  gift: "https://images.unsplash.com/photo-1655759738542-7249f691f27d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  coach: "https://images.unsplash.com/photo-1643373976950-f61780204e19?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  golf: "https://images.unsplash.com/photo-1591491653056-4313c0e2f379?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  coachPhone: "https://images.unsplash.com/photo-1646514301195-7fd7e262161c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
  morning: "https://images.unsplash.com/photo-1761389294347-4e7b17731a5b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
  open: "https://images.unsplash.com/photo-1715754614767-06334a0af608?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  adelaida: "/coaches/adelaida_calm.webp",
};

export const Eyebrow = ({ children, light, className = "" }) => (
  <p className={`text-[0.62rem] sm:text-[0.66rem] uppercase tracking-[0.38em] ${className}`} style={{ fontFamily: SANS, color: light ? S.gold : S.bronze }}>{children}</p>
);

export const Reveal = ({ children, delay = 0, className = "", y = 22 }) => (
  <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

export const H2 = ({ children, className = "" }) => (
  <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] ${className}`} style={{ fontFamily: SERIF }}>{children}</h2>
);

export const GoldButton = ({ children, onClick, testId, className = "" }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`min-h-[52px] rounded-full px-7 py-3.5 uppercase inline-flex items-center justify-center gap-2 transition-[transform,opacity] duration-300 hover:-translate-y-0.5 active:scale-[0.98] ${className}`}
    style={{ background: S.gold, color: S.night, fontFamily: SANS, fontSize: "0.7rem", letterSpacing: "0.24em", fontWeight: 600 }}>{children}</button>
);

export const GhostButton = ({ children, onClick, testId, dark, className = "" }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`min-h-[52px] rounded-full px-7 py-3.5 uppercase inline-flex items-center justify-center gap-2 transition-[background-color] duration-300 ${dark ? "hover:bg-black/5" : "hover:bg-white/10"} ${className}`}
    style={{ border: `1px solid ${dark ? S.line : "rgba(245,236,217,0.4)"}`, color: dark ? S.ink : S.cream, fontFamily: SANS, fontSize: "0.7rem", letterSpacing: "0.24em", fontWeight: 600 }}>{children}</button>
);

const BG = { light: S.ivory, sand: S.sand, deep: S.deep, navy: S.navy, dark: S.night };
export const Section = ({ children, tone = "light", id, testId, className = "" }) => (
  <section id={id} data-testid={testId} className={`relative px-6 sm:px-10 lg:px-20 ${className}`}
    style={{ background: BG[tone], color: tone === "light" || tone === "sand" ? S.ink : S.cream }}>{children}</section>
);

export const Grain = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
    style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
);

export const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Photo = ({ src, alt = "", className = "", ratio = "aspect-[4/3]", testId, pos = "center" }) => (
  <div className={`relative overflow-hidden rounded-2xl ${ratio} ${className}`} data-testid={testId}>
    <img src={src} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: pos }} />
  </div>
);
